/**
 * SleepAnalysisPage
 *
 * Detailed sleep analysis page with charts and pattern recognition.
 * Premium users get full access, free users see basic stats.
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  BarChart3,
  Lock,
  Loader2,
  TrendingUp,
  Clock,
  Moon,
  Sun,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useLimits } from '@/hooks/useLimits';
import { useSleepPlan } from '../hooks/useSleepPlan';
import { MIN_CYCLES_FOR_PERSONALIZATION } from '../lib/sleepScoring';

import { SleepPatternCircle } from '../components/SleepPatternCircle';

type TimeRange = '7days' | '30days';

export function SleepAnalysisPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('sleepPlan');
  const { getNumberLimit } = useLimits();
  const maxAnalysisDays = getNumberLimit('sleep_plan_analysis_days');

  const {
    isLoading,
    isPremium,
    settings,
    data,
    analysisData,
    patternData,
    recentCycleCount,
    loadAnalysis,
    loadPatternClusters,
  } = useSleepPlan();

  const can7Days = maxAnalysisDays >= 7;
  const can30Days = maxAnalysisDays >= 30;

  const [timeRange, setTimeRange] = useState<TimeRange>('7days');
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);

  // Load 14-day pattern clusters for the 24h circle on mount
  useEffect(() => {
    if (!settings?.isActive) return;
    loadPatternClusters(14);
  }, [settings?.isActive, loadPatternClusters]);

  // Load analysis data when time range changes
  useEffect(() => {
    if (!settings?.isActive) return;

    const days = timeRange === '7days' ? 7 : 30;

    // Check limit: user must have enough analysis days
    if (days > maxAnalysisDays) return;

    setIsLoadingAnalysis(true);
    loadAnalysis(days).finally(() => setIsLoadingAnalysis(false));
  }, [timeRange, settings?.isActive, maxAnalysisDays, loadAnalysis]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (!settings?.isActive) {
    navigate('/tools/sleep-plan');
    return null;
  }

  const timeRanges: { key: TimeRange; label: string }[] = [
    { key: '7days', label: t('analysis.7days') },
    { key: '30days', label: t('analysis.30days') },
  ];

  // Format values
  const totalSleep = analysisData
    ? `${Math.floor((analysisData.avgNapDurationMinutes ?? 0) * (analysisData.avgNapsPerDay ?? 0) / 60)}h ${Math.floor((analysisData.avgNapDurationMinutes ?? 0) * (analysisData.avgNapsPerDay ?? 0) % 60)}m`
    : data
      ? `${Math.floor(data.todayTotalSleepMinutes / 60)}h ${data.todayTotalSleepMinutes % 60}m`
      : '-';

  const napCount = analysisData?.avgNapsPerDay != null
    ? `${Math.round(analysisData.avgNapsPerDay)}`
    : data?.todayNapCount != null
      ? `${data.todayNapCount}`
      : '-';

  const avgNap = analysisData?.avgNapDurationMinutes != null
    ? `${analysisData.avgNapDurationMinutes}m`
    : data?.avgNapDurationMinutes
      ? `${data.avgNapDurationMinutes}m`
      : '-';

  const avgWakeWindow = analysisData?.avgWakeWindowMinutes != null
    ? `${analysisData.avgWakeWindowMinutes}m`
    : '-';

  const recommendedRange = data?.wakeWindow
    ? `${data.wakeWindow.minMinutes}-${data.wakeWindow.maxMinutes}m`
    : null;

  const kpiRows = [
    { icon: Moon, iconColor: 'text-white/50', label: t('analysis.totalSleep'), value: totalSleep },
    { icon: Sun, iconColor: 'text-white/50', label: analysisData ? t('analysis.napsPerDay') : t('analysis.napsToday'), value: napCount },
    { icon: Clock, iconColor: 'text-white/50', label: t('analysis.avgNap'), value: avgNap },
    { icon: TrendingUp, iconColor: 'text-white/50', label: t('analysis.avgWakeWindow'), value: avgWakeWindow, sub: recommendedRange ? t('analysis.recommended', { value: recommendedRange }) : undefined },
  ];

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-white text-xl font-bold">{t('analysis.title')}</h1>
      </div>

      {/* 24h Sleep Pattern Circle (always based on last 14 days) */}
      {(() => {
        const hasNapClusters = patternData?.clusters.some(c => c.sleepType === 'nap') ?? false;
        return (
          <div className="mb-6">
            <SleepPatternCircle
              clusters={patternData?.clusters ?? []}
              quietHoursStart={settings?.quietHoursStart}
              quietHoursEnd={settings?.quietHoursEnd}
              disabled={!hasNapClusters}
            />
            {!hasNapClusters && (
              <div className="text-center mt-2 px-8">
                <p className="text-white/40 text-xs">
                  {t('analysis.timelineNoData')}
                </p>
                {recentCycleCount < MIN_CYCLES_FOR_PERSONALIZATION && (
                  <div className="max-w-[200px] mx-auto mt-2">
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${Math.min(100, (recentCycleCount / MIN_CYCLES_FOR_PERSONALIZATION) * 100)}%`,
                          backgroundColor: '#F59E11',
                        }}
                      />
                    </div>
                    <p className="text-white/30 text-[10px] mt-1">
                      {t('learnProgress.remaining', {
                        count: Math.max(0, MIN_CYCLES_FOR_PERSONALIZATION - recentCycleCount),
                      })}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Time Range Tabs */}
      <div className="flex gap-2 mb-6">
        {timeRanges.map(range => {
          const isLocked = range.key === '7days' ? !can7Days : !can30Days;
          return (
            <button
              key={range.key}
              onClick={() => {
                if (isLocked) return;
                setTimeRange(range.key);
              }}
              className={`flex-1 py-2 rounded-[10px] text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
                timeRange === range.key && !isLocked
                  ? 'bg-pp-orange text-white'
                  : isLocked
                    ? 'bg-white/5 text-white/30'
                    : 'bg-white/10 text-white/70 hover:bg-white/15'
              }`}
            >
              {isLocked && <Lock className="w-3 h-3" />}
              {range.label}
            </button>
          );
        })}
      </div>

      {isLoadingAnalysis ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-white" />
        </div>
      ) : (
        <>
          {/* KPI Rows */}
          <div className="mb-6">
            {kpiRows.map((row, i) => (
              <div key={i}>
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                      <row.icon className={`w-4 h-4 ${row.iconColor}`} />
                    </div>
                    <div>
                      <span className="text-white/70 text-sm">{row.label}</span>
                      {row.sub && (
                        <p className="text-white/40 text-xs">{row.sub}</p>
                      )}
                    </div>
                  </div>
                  <span className="text-white text-2xl font-bold">{row.value}</span>
                </div>
                {i < kpiRows.length - 1 && (
                  <div className="h-px bg-white/25" />
                )}
              </div>
            ))}
          </div>

          {/* Weekly Trend - Premium only */}
          {analysisData?.dailyData && analysisData.dailyData.length > 0 && (
            <div className="glass-effect rounded-[15px] p-4 mb-6">
              <h3 className="text-white font-medium text-sm mb-3">{t('analysis.weeklyTrend')}</h3>
              <div className="flex items-end gap-1 h-24">
                {analysisData.dailyData.map((day, i) => {
                  const maxSleep = Math.max(...analysisData.dailyData.map(d => d.totalSleepMinutes));
                  const height = maxSleep > 0 ? (day.totalSleepMinutes / maxSleep) * 100 : 0;
                  const dayLabel = new Date(day.date).toLocaleDateString('de-DE', { weekday: 'short' });

                  return (
                    <div key={i} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full rounded-t-sm bg-pp-teal/60 transition-all"
                        style={{ height: `${Math.max(2, height)}%` }}
                      />
                      <span className="text-white/30 text-xs mt-1">{dayLabel}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Premium Upsell */}
          {!isPremium && (
            <div className="glass-effect rounded-[15px] p-4 mb-6 border border-pp-orange/30">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-pp-orange" />
                <div>
                  <p className="text-white font-medium text-sm">{t('analysis.premiumRequired')}</p>
                  <p className="text-white/60 text-xs">{t('analysis.premiumHint')}</p>
                </div>
              </div>
              <Link
                to="/subscription"
                className="block mt-3 py-2 bg-pp-orange rounded-[10px] text-white text-sm text-center font-medium"
              >
                Premium
              </Link>
            </div>
          )}

          {/* Patterns - Premium only */}
          {isPremium && analysisData && (
            <div className="glass-effect rounded-[15px] p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-pp-teal" />
                <h3 className="text-white font-medium text-sm">{t('analysis.patterns')}</h3>
              </div>
              {analysisData.avgNapDurationMinutes != null ? (
                <div className="space-y-2">
                  <p className="text-white/70 text-sm">
                    {t('analysis.personalizedWindow', {
                      min: Math.max(0, (analysisData.avgWakeWindowMinutes ?? 0) - 15),
                      max: (analysisData.avgWakeWindowMinutes ?? 0) + 15,
                    })}
                  </p>
                  {analysisData.avgNapDurationMinutes > 0 && (
                    <p className="text-white/70 text-sm">
                      {t('analysis.patternFirstNapLongest', {
                        minutes: analysisData.avgNapDurationMinutes,
                      })}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-white/50 text-sm">{t('analysis.noPatterns')}</p>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
